import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowDown,
  ArrowUp,
  Download,
  GripVertical,
  RotateCcw,
  X,
} from "lucide-react";

/**
 * Kolomkeuze voor de Excel-export van inschrijvingen.
 *
 * Waarom dit bestaat: json_to_sheet leidde de kolommen af uit de eerste
 * inschrijving, en inschrijfformulieren hebben voorwaardelijke velden (adres
 * enkel bij niet-leden, beschikbaarheid enkel wanneer ingevuld). Velden die pas
 * in een latere rij opdoken, belandden daardoor achteraan en stonden los van de
 * velden waar ze bij horen. Nu ligt de volgorde vast — en kan de club ze zelf
 * kiezen.
 */

// De vaste velden die niet uit het formulier komen maar uit de inschrijving zelf.
const metaColumns = (statusLabel) => [
  {
    key: "__registratiedatum",
    label: "Registratiedatum",
    get: (reg, helpers) => helpers.formatDate(reg.created_at),
  },
  {
    key: "__status",
    label: "Status",
    get: (reg) => statusLabel(reg.status),
  },
  {
    key: "__betaalwijze",
    label: "Betaalwijze",
    get: (reg) =>
      reg.payment_timing === "later"
        ? "Later te betalen"
        : reg.payment_timing === "immediate"
          ? "Online (Mollie)"
          : "",
  },
  {
    key: "__bedrag",
    label: "Bedrag",
    get: (reg) => (reg.amount != null ? Number(reg.amount) : ""),
  },
  {
    key: "__lid",
    label: "Lid",
    get: (reg) => (reg.is_member == null ? "" : reg.is_member ? "Ja" : "Nee"),
  },
];

// Logische volgorde van de velden die inschrijfformulieren gangbaar gebruiken:
// eerst wie het is, dan wat die persoon volgt, dan waar die woont, dan hoe we
// hem bereiken. Alles wat we niet kennen, volgt daarna in de volgorde van het
// formulier zelf.
const FIELD_ORDER = [
  "voornaam",
  "naam",
  "achternaam",
  "volledige_naam",
  "geboortedatum",
  "leeftijd",
  "geslacht",
  "lidstatus",
  "niveau",
  "lesvorm",
  "formule",
  "lesmomenten",
  "beschikbare_dagen",
  "beschikbaarheid",
  "straat",
  "huisnummer",
  "postcode",
  "gemeente",
  "contact_voornaam",
  "contact_naam",
  "email",
  "mail",
  "telefoon",
  "gsm",
  "opmerkingen",
  "akkoord_reglement",
];

const fieldRank = (key) => {
  const index = FIELD_ORDER.indexOf(key.toLowerCase());
  return index === -1 ? FIELD_ORDER.length : index;
};

// "contact_voornaam" -> "Contact voornaam"
const prettify = (key) => {
  const spaced = key.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Alle beschikbare kolommen over álle inschrijvingen heen, in de standaard-
 * volgorde. Elke kolom weet zelf hoe ze haar waarde uit een inschrijving haalt.
 */
export const buildExportColumns = (registrations, statusLabel) => {
  const dataKeys = [];
  for (const reg of registrations) {
    for (const key of Object.keys(reg.data || {})) {
      if (!dataKeys.includes(key)) dataKeys.push(key);
    }
  }

  const dataColumns = dataKeys
    .map((key, index) => ({ key, index }))
    // Onbekende velden houden hun onderlinge formuliervolgorde: alleen de
    // velden die we kennen worden naar hun vaste plaats getild.
    .sort((a, b) => fieldRank(a.key) - fieldRank(b.key) || a.index - b.index)
    .map(({ key }) => ({
      key,
      label: prettify(key),
      get: (reg) => {
        const value = reg.data?.[key];
        if (value == null) return "";
        return Array.isArray(value) ? value.join(", ") : value;
      },
    }));

  return [...dataColumns, ...metaColumns(statusLabel)];
};

const storageKey = (eventId) => `zichtbaar:export-columns:${eventId}`;

// De bewaarde keuze is een momentopname: velden kunnen intussen bijgekomen of
// verdwenen zijn. We volgen de bewaarde volgorde voor wat we terugvinden en
// hangen nieuwe velden achteraan, zodat een gewijzigd formulier de keuze niet
// stukmaakt.
const mergeWithSaved = (columns, saved) => {
  if (!saved || !Array.isArray(saved.order)) {
    return columns.map((c) => ({ ...c, enabled: true }));
  }
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const merged = [];
  for (const key of saved.order) {
    const column = byKey.get(key);
    if (column) {
      merged.push({ ...column, enabled: !saved.hidden?.includes(key) });
      byKey.delete(key);
    }
  }
  for (const column of columns) {
    if (byKey.has(column.key)) merged.push({ ...column, enabled: true });
  }
  return merged;
};

export default function RegistrationExportDialog({
  isDark,
  columns,
  eventId,
  count,
  onExport,
  onClose,
}) {
  const [items, setItems] = useState(() => columns.map((c) => ({ ...c, enabled: true })));
  const [dragKey, setDragKey] = useState(null);

  useEffect(() => {
    let saved = null;
    try {
      const raw = window.localStorage.getItem(storageKey(eventId));
      if (raw) saved = JSON.parse(raw);
    } catch {
      // Een stukke of geblokkeerde localStorage mag de export niet tegenhouden.
    }
    setItems(mergeWithSaved(columns, saved));
  }, [columns, eventId]);

  const selected = useMemo(() => items.filter((i) => i.enabled), [items]);

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  };

  const moveTo = (fromKey, toKey) => {
    if (fromKey === toKey) return;
    const from = items.findIndex((i) => i.key === fromKey);
    const to = items.findIndex((i) => i.key === toKey);
    if (from === -1 || to === -1) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
  };

  const toggle = (key) =>
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, enabled: !i.enabled } : i)),
    );

  const reset = () => setItems(columns.map((c) => ({ ...c, enabled: true })));

  const confirm = () => {
    if (!selected.length) return;
    try {
      window.localStorage.setItem(
        storageKey(eventId),
        JSON.stringify({
          order: items.map((i) => i.key),
          hidden: items.filter((i) => !i.enabled).map((i) => i.key),
        }),
      );
    } catch {
      // Niet kunnen onthouden is vervelend, maar geen reden om niet te exporteren.
    }
    onExport(selected);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`relative flex max-h-[85vh] w-full max-w-md flex-col rounded-2xl border ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200"}`}
      >
        <div
          className={`flex items-start justify-between gap-4 border-b px-5 py-4 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <div>
            <h3
              className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Exporteren naar Excel
            </h3>
            <p
              className={`mt-0.5 text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              {count} inschrijving{count === 1 ? "" : "en"} · kies welke
              kolommen mee moeten en in welke volgorde.
            </p>
          </div>
          <button
            onClick={onClose}
            className={`rounded-lg p-1 transition-colors ${isDark ? "text-gray-500 hover:bg-white/[0.04] hover:text-gray-300" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          <ul className="space-y-1">
            {items.map((item, index) => (
              <li
                key={item.key}
                draggable
                onDragStart={() => setDragKey(item.key)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragKey) moveTo(dragKey, item.key);
                }}
                onDragEnd={() => setDragKey(null)}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${
                  dragKey === item.key
                    ? isDark
                      ? "bg-white/[0.06]"
                      : "bg-gray-100"
                    : isDark
                      ? "hover:bg-white/[0.03]"
                      : "hover:bg-gray-50"
                }`}
              >
                <GripVertical
                  className={`h-3.5 w-3.5 shrink-0 cursor-grab ${isDark ? "text-gray-600" : "text-gray-300"}`}
                />
                <label className="flex flex-1 cursor-pointer items-center gap-2.5 overflow-hidden">
                  <input
                    type="checkbox"
                    checked={item.enabled}
                    onChange={() => toggle(item.key)}
                    className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 accent-blue-600"
                  />
                  <span
                    className={`truncate text-[13px] ${
                      item.enabled
                        ? isDark
                          ? "text-gray-200"
                          : "text-gray-700"
                        : isDark
                          ? "text-gray-600 line-through"
                          : "text-gray-400 line-through"
                    }`}
                  >
                    {item.label}
                  </span>
                </label>
                {/* Pijltjes naast het slepen: op een touchscreen is slepen in
                    een scrollende lijst geen doen. */}
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={`${item.label} omhoog`}
                    className={`rounded p-1 transition-colors disabled:opacity-20 ${isDark ? "text-gray-500 hover:bg-white/[0.06] hover:text-gray-300" : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"}`}
                  >
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1}
                    aria-label={`${item.label} omlaag`}
                    className={`rounded p-1 transition-colors disabled:opacity-20 ${isDark ? "text-gray-500 hover:bg-white/[0.06] hover:text-gray-300" : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"}`}
                  >
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div
          className={`flex items-center justify-between gap-3 border-t px-5 py-3 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <button
            onClick={reset}
            className={`flex items-center gap-1.5 text-[12px] font-medium transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
          >
            <RotateCcw className="h-3 w-3" />
            Standaard
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`h-8 rounded-lg border px-4 text-[12px] font-medium transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Annuleren
            </button>
            <button
              onClick={confirm}
              disabled={!selected.length}
              className="flex h-8 items-center gap-2 rounded-lg bg-blue-600 px-4 text-[12px] font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-blue-600"
            >
              <Download className="h-3.5 w-3.5" />
              Exporteren ({selected.length})
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
